import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const FAQPage = () => {
  // Estado para rastrear qué pregunta está actualmente seleccionada
  const [activeIndex, setActiveIndex] = useState(null);
  
  // Estado para el término de búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para almacenar categorías filtradas
  const [filteredCategories, setFilteredCategories] = useState([]);

  // Función para manejar el toggle de las preguntas
  const toggleQuestion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Datos de las preguntas frecuentes
  const faqCategories = [
    {
      category: "Sobre Stupid Comedy",
      questions: [
        {
          question: "¿Qué es Stupid Comedy?",
          answer: "Somos una plataforma de streaming chilena especializada en comedia y stand-up. Ofrecemos rutinas de comediantes nacionales e internacionales, sketches originales y shows exclusivos que no encontrarás en ningún otro lugar."
        },
        {
          question: "¿Cuándo fue fundada Stupid Comedy?",
          answer: "Stupid Comedy fue fundada en 2025 con la misión de crear un espacio dedicado exclusivamente al humor y apoyar a los talentos emergentes del stand-up chileno."
        },
        {
          question: "¿Qué nos hace diferentes?",
          answer: "Nos distinguimos por ofrecer contenido 100% enfocado en comedia, producir shows exclusivos, y mantener una conexión directa entre comediantes y audiencia. Somos la primera plataforma chilena especializada exclusivamente en stand-up comedy."
        }
      ]
    },
    {
      category: "Suscripciones y Pagos",
      questions: [
        {
          question: "¿Cuánto cuesta la suscripción?",
          answer: "Actualmente ofrecemos tres planes: Plan Básico a $4.990 mensuales, Plan Premium a $7.990 mensuales y Plan Anual a $59.990 (equivalente a $4.999 por mes). Cada plan incluye diferentes beneficios y niveles de acceso."
        },
        {
          question: "¿Qué métodos de pago aceptan?",
          answer: "Ocupamos la plataforma de pagos Flow, que acepta tarjetas de crédito y débito, transferencias bancarias y pagos en efectivo a través de puntos de venta autorizados."
        },
        {
          question: "¿Ofrecen período de prueba gratuito?",
          answer: "Sí, todos los nuevos usuarios pueden disfrutar de 7 días de prueba gratuita con acceso completo a nuestra plataforma. Solo necesitas registrarte con tu correo electrónico y un método de pago válido."
        },
        {
          question: "¿Puedo cancelar mi suscripción en cualquier momento?",
          answer: "Absolutamente. Puedes cancelar tu suscripción cuando quieras desde tu perfil de usuario. No hay cargos por cancelación y podrás seguir disfrutando del servicio hasta el final del período facturado."
        }
      ]
    },
    {
      category: "Contenido y Programación",
      questions: [
        {
          question: "¿Qué tipo de contenido ofrecen?",
          answer: "Ofrecemos especiales de stand-up, sketches de comedia, shows completos, podcasts humorísticos y transmisiones en vivo de eventos."
        },
        {
          question: "¿Con qué frecuencia agregan nuevo contenido?",
          answer: "Agregamos nuevo contenido semanalmente."
        },
        {
          question: "¿Realizan transmisiones en vivo?",
          answer: "Sí, transmitimos shows en vivo. Además, organizamos eventos especiales que son anunciados con anticipación en nuestra plataforma y redes sociales."
        },
      ]
    },
    {
      category: "Aspectos Técnicos",
      questions: [
        {
          question: "¿En qué dispositivos puedo ver Stupid Comedy?",
          answer: "Puedes acceder a nuestra plataforma desde navegadores web, smartphones, tablets, smart TVs con navegador."
        },
        {
          question: "¿Cuál es la calidad de video disponible?",
          answer: "Ofrecemos streaming en calidad Full HD (1080p)."
        },
        {
          question: "¿Qué hago si tengo problemas técnicos?",
          answer: "Puedes consultar nuestra sección de soporte técnico o contactarnos directamente a contacto@stupidcomedy.tv. Nuestro equipo está disponible de lunes a viernes de 9:00 a 20:00 hrs para ayudarte a resolver cualquier inconveniente."
        }
      ]
    },
    {
      category: "Eventos y Shows Presenciales",
      questions: [
        {
          question: "¿Puedo comprar entradas para shows presenciales?",
          answer: "Sí, vendemos entradas para shows presenciales a través de nuestra plataforma."
        },
        {
          question: "¿Organizan eventos fuera de Santiago?",
          answer: "Actualmente organizamos eventos en Santiago. Estamos trabajando para expandirnos a más ciudades durante el próximo año."
        },
        {
          question: "¿Cómo puedo proponer mi local para realizar un show?",
          answer: "Si tienes un local y estás interesado en organizar shows de comedia, puedes contactarnos en contacto@stupidcomedy.tv con los detalles de tu espacio."
        }
      ]
    }
  ];

  // Función para filtrar categorías y preguntas basadas en el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      // Si no hay término de búsqueda, mostrar todas las categorías
      setFilteredCategories(faqCategories);
      return;
    }

    // Convertir término de búsqueda a minúsculas para comparación no sensible a mayúsculas
    const term = searchTerm.toLowerCase();
    
    // Filtrar las categorías y preguntas que coinciden con el término de búsqueda
    const filtered = faqCategories
      .map(category => {
        // Filtrar preguntas dentro de esta categoría
        const filteredQuestions = category.questions.filter(
          item => 
            item.question.toLowerCase().includes(term) || 
            item.answer.toLowerCase().includes(term)
        );
        
        // Solo incluir esta categoría si tiene preguntas que coinciden
        if (filteredQuestions.length > 0) {
          return {
            ...category,
            questions: filteredQuestions
          };
        }
        return null;
      })
      .filter(Boolean); // Eliminar categorías nulas (sin preguntas coincidentes)
    
    setFilteredCategories(filtered);
    
    // Si hay resultados de búsqueda, abrir automáticamente la primera pregunta coincidente
    if (filtered.length > 0 && filtered[0].questions.length > 0 && searchTerm.length > 2) {
      setActiveIndex(`0-0`);
    }
  }, [searchTerm]);

  // Manejar cambios en el input de búsqueda
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Limpiar búsqueda
  const clearSearch = () => {
    setSearchTerm('');
  };

  return (
    <>
      <Helmet>
        <title>Preguntas Frecuentes | Stupid Comedy</title>
        <meta name="description" content="Encuentra respuestas a todas tus dudas sobre Stupid Comedy, la plataforma de streaming especializada en comedia y stand-up chileno." />
      </Helmet>
      
      <section className="py-12 bg-gradient-to-b from-black to-gray-900 text-white ">
        {/* Encabezado */}
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-12 border-b border-orange-500 pb-4">
            <motion.h1 
              className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-amber-500"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Preguntas Frecuentes
            </motion.h1>
            <div className="flex items-center mt-4 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Documento informativo vigente</span>
            </div>
          </div>

          <div className="prose prose-lg prose-invert max-w-none">
            {/* Buscador con funcionalidad */}
            <div className="mb-10 p-6 bg-gray-800 bg-opacity-50 rounded-xl shadow-lg border-l-4 border-orange-500">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Buscar una pregunta..." 
                  className="w-full py-3 px-5 rounded-lg bg-black bg-opacity-50 text-white placeholder-gray-400 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm ? (
                  <button 
                    onClick={clearSearch}
                    className="absolute right-12 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                ) : null}
                <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-orange-400 hover:text-orange-300 transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>

              {/* Mostrar información sobre resultados de búsqueda */}
              {searchTerm && (
                <div className="mt-4 text-gray-300">
                  {filteredCategories.length === 0 ? (
                    <p className="text-center text-orange-300">No se encontraron resultados para "{searchTerm}"</p>
                  ) : (
                    <p className="text-center">
                      {(() => {
                        const totalQuestions = filteredCategories.reduce(
                          (count, category) => count + category.questions.length, 
                          0
                        );
                        return `Se encontraron ${totalQuestions} resultado${totalQuestions !== 1 ? 's' : ''}`;
                      })()}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Mostrar mensaje si no hay resultados */}
            {searchTerm && filteredCategories.length === 0 && (
              <div className="flex flex-col items-center justify-center p-8 mb-10">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xl text-center">No encontramos respuestas para tu búsqueda</p>
                <p className="text-gray-400 text-center mt-2">Intenta con otros términos o revisa nuestras categorías</p>
                <button
                  onClick={clearSearch}
                  className="mt-6 px-4 py-2 bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Borrar búsqueda
                </button>
              </div>
            )}

            {/* Preguntas frecuentes por categoría */}
            {filteredCategories.map((category, catIndex) => (
              <motion.div 
                key={catIndex}
                className="mb-10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: catIndex * 0.1 }}
              >
                <h3 className="text-2xl font-semibold mb-4 text-orange-300">{category.category}</h3>
                <div className="space-y-4">
                  {category.questions.map((item, qIndex) => (
                    <div 
                      key={qIndex}
                      className={`p-6 bg-gray-800 bg-opacity-50 rounded-xl shadow-lg hover:shadow-orange-900/20 transition duration-300 border-l-4 ${
                        searchTerm && 
                        (item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         item.answer.toLowerCase().includes(searchTerm.toLowerCase()))
                          ? 'border-yellow-400'
                          : 'border-orange-500'
                      }`}
                    >
                      <button
                        className="w-full text-left font-semibold text-lg flex justify-between items-center"
                        onClick={() => toggleQuestion(`${catIndex}-${qIndex}`)}
                      >
                        <span className={searchTerm && item.question.toLowerCase().includes(searchTerm.toLowerCase()) ? 'bg-yellow-900/20 px-1' : ''}>
                          {item.question}
                        </span>
                        <span className={`transform transition-transform duration-300 ${activeIndex === `${catIndex}-${qIndex}` ? 'rotate-180' : ''}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </button>
                      
                      <div 
                        className="overflow-hidden transition-all duration-300"
                        style={{ 
                          maxHeight: activeIndex === `${catIndex}-${qIndex}` ? '1000px' : '0',
                          opacity: activeIndex === `${catIndex}-${qIndex}` ? 1 : 0,
                          marginTop: activeIndex === `${catIndex}-${qIndex}` ? '1rem' : '0'
                        }}
                      >
                        <p className="text-gray-300">{item.answer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}

            {/* Mostrar CTA solo si no hay búsqueda o si hay resultados */}
            {(!searchTerm || filteredCategories.length > 0) && (
              <div className="mt-16 p-6 bg-orange-900 bg-opacity-20 rounded-xl border border-orange-700 max-w-2xl mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-orange-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-center">¿No encontraste lo que buscabas?</h3>
                <p className="text-center text-gray-300 mb-6">
                  Si tienes alguna pregunta adicional o necesitas más información, no dudes en contactarnos.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link to="/contacto" className="px-8 py-3 bg-gradient-to-r from-orange-600 to-amber-600 text-white font-bold rounded-lg hover:from-orange-700 hover:to-amber-700 transition-all text-center">
                    Contactar Soporte
                  </Link>
                  <a href="mailto:contacto@stupidcomedy.tv" className="px-8 py-3 bg-transparent border border-orange-500 text-orange-400 font-bold rounded-lg hover:bg-orange-900/20 transition-colors text-center">
                    contacto@stupidcomedy.tv
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQPage;